import React, { useState, useEffect, useMemo } from "react";
import AzottePagination from "./AzottePagination";
import {
    CircularProgress,
    Collapse,
    IconButton,
    LinearProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    Stack,
    Divider,
    Chip,
    Grid,
} from "@mui/material";
import { alpha, Box } from "@mui/system";
import { AzotteGridRowContext } from "./AzotteGridDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";

/**
 * Filter columns based on viewport:
 *  - hideOnMobile: column hidden on phone-size screens
 *  - hideOnTablet: column hidden on tablet-size screens
 */
const filterColumnsForViewport = (columns, viewport) => {
    return columns.filter((col) => {
        if (viewport === "mobile" && col.hideOnMobile) return false;
        if (viewport === "tablet" && col.hideOnTablet) return false;
        return true;
    });
};

// Mobile card view for a single row (phone: 12-col grid; columns can opt into half/full width via `mobileColSpan` or `mobileFullWidth`)
const MobileRowCard = ({ row, columns, children, expanded, toggleExpand, onDoubleClick, renderDetails, keyField, actionColumnKey }) => {
    const isOpen = expanded.has(row.id);

    const actionCol = columns.find((col) => col.key === actionColumnKey);
    const dataCols = columns.filter((col) => col.key !== actionColumnKey);

    return (
        <Card
            variant="outlined"
            sx={{
                mb: 2,
                borderRadius: 2,
                '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 1,
                }
            }}
            onDoubleClick={() => onDoubleClick && onDoubleClick(row)}
        >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Grid container spacing={1.25}>
                    {dataCols.map((col) => {
                        const value = col.render ? col.render(row[col.key], row) : row[col.key] ?? "";
                        const span = col.mobileFullWidth ? 12 : (col.mobileColSpan ?? 6);
                        return (
                            <Grid key={col.key} size={span}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            fontWeight: 600,
                                            fontSize: '0.65rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {typeof col.header === "function" ? col.header() : col.header}
                                    </Typography>
                                    <Box sx={{ fontSize: '0.875rem', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                        {value}
                                    </Box>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>

                {actionCol && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                        {actionCol.render ? actionCol.render(row[actionCol.key], row) : row[actionCol.key] ?? ""}
                    </Box>
                )}

                {/* Expandable details section */}
                {children && (
                    <>
                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <IconButton
                                size="small"
                                onClick={() => toggleExpand(row.id)}
                                aria-label={isOpen ? "collapse details" : "expand details"}
                            >
                                {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        </Box>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 1 }}>
                                {renderDetails(row)}
                            </Box>
                        </Collapse>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

// Tablet card view for a single row (2-column grid layout for better space usage)
const TabletRowCard = ({ row, columns, children, expanded, toggleExpand, onDoubleClick, renderDetails, keyField, actionColumnKey }) => {
    const isOpen = expanded.has(row.id);

    // Separate action column from data columns
    const actionCol = columns.find((col) => col.key === actionColumnKey);
    const dataCols = columns.filter((col) => col.key !== actionColumnKey);

    return (
        <Card
            variant="outlined"
            sx={{
                mb: 2,
                borderRadius: 2,
                '&:hover': {
                    borderColor: 'primary.main',
                    boxShadow: 1,
                },
            }}
            onDoubleClick={() => onDoubleClick && onDoubleClick(row)}
        >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {/* Action button row - top right */}
                {actionCol && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        {actionCol.render ? actionCol.render(row[actionCol.key], row) : row[actionCol.key] ?? ""}
                    </Box>
                )}

                {/* 2-column grid for data fields */}
                <Grid container spacing={2}>
                    {dataCols.map((col, idx) => {
                        const value = col.render ? col.render(row[col.key], row) : row[col.key] ?? "";
                        // First column or columns marked as "primary" span full width
                        const isWide = col.tabletFullWidth || idx === 0;

                        return (
                            <Grid
                                key={col.key}
                                size={{
                                    xs: 12,
                                    sm: isWide ? 12 : 6
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: 'text.secondary',
                                            fontWeight: 300,
                                            fontSize: '0.7rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                        }}
                                    >
                                        {typeof col.header === "function" ? col.header() : col.header}
                                    </Typography>
                                    <Box sx={{ fontSize: '0.875rem', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                        {value}
                                    </Box>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Expandable details section */}
                {children && (
                    <>
                        <Divider sx={{ my: 1.5 }} />
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <IconButton
                                size="small"
                                onClick={() => toggleExpand(row.id)}
                                aria-label={isOpen ? "collapse details" : "expand details"}
                            >
                                {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        </Box>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 1 }}>
                                {renderDetails(row)}
                            </Box>
                        </Collapse>
                    </>
                )}
            </CardContent>
        </Card>
    );
};


const AzotteGrid = ({
    columns = [],
    rows = [],
    totalCount = 0,
    rowsPerPageOptions = [5, 10, 25],
    page = 0,
    setPage = () => { },
    rowsPerPage = 10,
    handleRowsPerPageChange = () => { },
    fetchData = () => { },
    loading = false,
    error = "",
    children,
    detailsSx = {},
    sx = {},
    showPagination = true,
    showToolbar = true,
    onDoubleClick,
    onAdd,
    title,
    keyField = "id",
    actionColumnKey = "actions",
    showToolBarOnAction = false,
    mobileBreakpoint = "sm", // Breakpoint for mobile card view (phone)
    tabletBreakpoint = "md", // Breakpoint for tablet card view
    responsiveMode = true, // Enable/disable responsive mode

}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));
    const isTablet = useMediaQuery(theme.breakpoints.between(mobileBreakpoint, tabletBreakpoint));
    const showMobileView = responsiveMode && isMobile;
    const showTabletView = responsiveMode && isTablet;
    const showCardView = showMobileView || showTabletView;

    // Determine current viewport label
    const viewport = showMobileView ? "mobile" : showTabletView ? "tablet" : "desktop";

    // Filter columns for current viewport
    const visibleColumns = useMemo(
        () => filterColumnsForViewport(columns, viewport),
        [columns, viewport]
    );

    const hasActionColumn = columns.some((col) => col.key === actionColumnKey);
    const showActionToolbar = hasActionColumn && showToolBarOnAction;
    // Show toolbar in card views even when showToolbar is false OR when showToolBarOnAction is true (since table header isn't visible)
    const shouldRenderToolbar = Boolean((showToolbar || showCardView) && (!hasActionColumn || !showToolBarOnAction || showCardView));
    // Ensure rows is always an array 
    rows = Array.isArray(rows) ? rows : [];
    const ActionButtons = React.memo(function ActionButtons({ fetchData, loading, onAdd }) {
        return (
            <>
                {onAdd && (
                    <Tooltip title="Add New Item">
                        <IconButton color="primary" onClick={onAdd}>
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                )}
                {fetchData && (
                    <Tooltip title="Refresh">
                        <span>
                            <IconButton
                                color="primary"
                                onClick={fetchData}
                                disabled={loading}
                                size="small"
                                sx={{ mr: 1 }}
                            >
                                <span style={{ fontSize: 20 }}>⟳</span>
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </>
        );
    });
    const GridToolbar = React.memo(({ fetchData, loading, onAdd, title }) => (
        <Toolbar>
            <Box sx={{ flexGrow: 1 }} >
                {title && (
                    <Typography variant="h6" component="div" sx={{ m: 0, p: 0 }}>
                        {title}
                    </Typography>
                )}
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
                <ActionButtons fetchData={fetchData} loading={loading} onAdd={onAdd} />
            </Box>
        </Toolbar>
    ));


    const toggleExpand = (id) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const [expanded, setExpanded] = useState(() => new Set());

    // Wrap details in a provider so children can access row via hook
    function renderDetails(row) {
        // If children is a function, call it with row
        const details = typeof children === "function"
            ? children({ row })
            : children;

        // Extract sx from details if present, otherwise use detailsSx
        const sx = (React.isValidElement(details) && details.props?.sx) ? details.props.sx : detailsSx;

        return (
            <AzotteGridRowContext.Provider value={row}>
                <Paper elevation={0} sx={sx}>
                    {details}
                </Paper>
            </AzotteGridRowContext.Provider>
        );
    }

    return (
        <React.Fragment>
            {loading && (
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "background.paper",
                        opacity: 0.8,
                        zIndex: 10,
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
            <Paper sx={{ p: showCardView ? 2 : 0, ...sx }}>
                {/* Toolbar - shared between mobile, tablet, and desktop */}
                {shouldRenderToolbar ? (
                    <GridToolbar
                        fetchData={fetchData}
                        loading={loading}
                        onAdd={onAdd}
                        title={title}
                    />
                ) : (
                    title && (
                        <Box sx={{ p: 1 }}>
                            <Typography variant="h6" component="div" sx={{ m: 0, p: 0 }}>{title}</Typography>
                        </Box>
                    )
                )}

                {/* Mobile View - Single-Column Card Layout */}
                {showMobileView ? (
                    <Box sx={{ mt: 1 }}>
                        {rows.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                                No data found.
                            </Box>
                        ) : (
                            rows.map((row) => (
                                <MobileRowCard
                                    key={row.id || row[keyField]}
                                    row={row}
                                    columns={visibleColumns}
                                    children={children}
                                    expanded={expanded}
                                    toggleExpand={toggleExpand}
                                    onDoubleClick={onDoubleClick}
                                    renderDetails={renderDetails}
                                    keyField={keyField}
                                    actionColumnKey={actionColumnKey}
                                />
                            ))
                        )}
                    </Box>
                ) : showTabletView ? (
                    /* Tablet View - Two-Column Grid Card Layout */
                    <Box sx={{ mt: 1 }}>
                        {rows.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                                No data found.
                            </Box>
                        ) : (
                            rows.map((row) => (
                                <TabletRowCard
                                    key={row.id || row[keyField]}
                                    row={row}
                                    columns={visibleColumns}
                                    children={children}
                                    expanded={expanded}
                                    toggleExpand={toggleExpand}
                                    onDoubleClick={onDoubleClick}
                                    renderDetails={renderDetails}
                                    keyField={keyField}
                                    actionColumnKey={actionColumnKey}
                                />
                            ))
                        )}
                    </Box>
                ) : (
                    /* Desktop View - Table Layout */
                    <Box sx={{ width: "100%", overflowX: "auto" }}>
                        <TableContainer>
                            <Table stickyHeader size="small" sx={{ minWidth: 650 }}>
                                <TableHead >
                                    <TableRow >
                                        {(children) && (
                                            <TableCell padding="checkbox" width={15}>
                                            </TableCell>
                                        )}
                                        {columns.map((col) => (
                                            <TableCell
                                                key={col.key}
                                                style={{
                                                    width: col.width,
                                                    minWidth: col.width,
                                                    maxWidth: col.width,
                                                    ...(col.flex && { padding: 0 }),
                                                    verticalAlign: "middle",
                                                    fontSize: "0.9em",
                                                }}
                                            >
                                                {(showActionToolbar && col.key === actionColumnKey) ? (
                                                    <ActionButtons
                                                        fetchData={fetchData}
                                                        loading={loading}
                                                        onAdd={onAdd}
                                                    />
                                                ) :
                                                    (
                                                        typeof col.header === "function"
                                                            ? col.header()
                                                            : col.header
                                                    )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((row) => {
                                        const isOpen = expanded.has(row.id);
                                        return (
                                            <React.Fragment key={row.id || row[keyField]}>
                                                <TableRow
                                                    hover
                                                    sx={{ cursor: "pointer", height: 48 }}
                                                    onDoubleClick={() => onDoubleClick && onDoubleClick(row)}
                                                >
                                                    {(children) && (
                                                        <TableCell padding="checkbox" width={15}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => toggleExpand(row.id)}
                                                                aria-label={
                                                                    isOpen ? "collapse row" : "expand row"
                                                                }
                                                            >
                                                                {isOpen ? (
                                                                    <ExpandLessIcon fontSize="small" />
                                                                ) : (
                                                                    <ExpandMoreIcon fontSize="small" />
                                                                )}
                                                            </IconButton>
                                                        </TableCell>
                                                    )}
                                                    {columns.map((col) => (
                                                        <React.Fragment key={col.key}>
                                                            <TableCell
                                                                key={col.key}
                                                                style={{
                                                                    width: col.width,
                                                                    minWidth: col.width,
                                                                    maxWidth: col.width,
                                                                    ...(col.flex && { padding: 0 }),
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    overflowWrap: "anywhere",
                                                                    wordBreak: "break-word",
                                                                    whiteSpace: "pre-line",
                                                                    verticalAlign: col.verticalAlign || "middle",
                                                                    fontSize: "0.9em",

                                                                }}
                                                            >

                                                                {col.render
                                                                    ? col.render(row[col.key], row)
                                                                    : row[col.key] ?? ""}
                                                            </TableCell>
                                                        </React.Fragment>
                                                    ))}
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell
                                                        style={{ padding: 0, margin: 0 }}
                                                        colSpan={columns.length + 2}
                                                        sx={(theme) => ({
                                                            backgroundColor: alpha(
                                                                theme.palette.secondary.main,
                                                                0.07
                                                            ),
                                                        })}
                                                    >
                                                        <Collapse
                                                            in={isOpen}
                                                            timeout="auto"
                                                            unmountOnExit
                                                        >
                                                            {children && renderDetails(row)}
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        );
                                    })}
                                    {rows.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={columns.length + (children ? 2 : 1)} align="center">
                                                No data found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                )}

                {showPagination !== false && (
                    <AzottePagination
                        page={page}
                        setPage={setPage}
                        totalCount={totalCount}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        rowsPerPageOptions={rowsPerPageOptions}
                    />
                )}
            </Paper>
        </React.Fragment>
    );
};

export default AzotteGrid;

